import React, { PropsWithChildren, useContext, useEffect } from "react";
import { useLineSlots } from "../context/LineViewTabsContext";
import _ from "lodash";
import { ConnectedSlot, SlotPlaceholder } from "./Slot";
import { GermanLicenseInfo } from "./GermanLicenseInfo";
import { LineupContext } from "../context/LineupContext";
import { ILineup, LineupPosition } from "../types";
import { useTranslation } from "@root/misc/useTranslation";
import { StatsIconLegend } from "./StatsIconLegend";
import { useDevice } from "@root/misc/device";

type LineupWithLicenseNumber = ILineup & {
  restrictions: {
    max_without_german_license: number;
  };
};

const showGermanLicenseInfo = (
  lineup: ILineup | undefined,
  numPickedOptionsWithoutGermanLicense: number
): lineup is LineupWithLicenseNumber => {
  return (
    !!lineup &&
    !lineup.from_other &&
    _.isNumber(lineup.restrictions.max_without_german_license) &&
    numPickedOptionsWithoutGermanLicense > 0
  );
};

const Row: React.FC<
  PropsWithChildren<{ pos: LineupPosition; shiftSecondItemABitDown?: boolean }>
> = ({ children, pos, shiftSecondItemABitDown }) => {
  const className = [
    "neo__lineupField__row",
    `neo__lineupField__row--${pos}`,
    shiftSecondItemABitDown
      ? "neo__lineupField__row--shiftSecondItemABitDown"
      : "",
  ].join(" ");
  return <div className={className}>{children}</div>;
};

export const ValidityInfo: React.FC<
  PropsWithChildren<{ valid: boolean; markCritical: boolean }>
> = ({ children, valid, markCritical }) => {
  const { t } = useTranslation();
  const className = [
    "neo__lineup__validityInfo",
    valid
      ? "neo__lineup__validityInfo--valid"
      : "neo__lineup__validityInfo--invalid",
    markCritical ? "neo__lineup__validityInfo--markCritical" : "",
  ].join(" ");
  return (
    <div className={className}>
      <p>{children}</p>
      {markCritical && (
        <p>
          {t("lineup.invalid_reason.note_only_valid_lineups_will_be_scored")}
        </p>
      )}
    </div>
  );
};

export const Field: React.FC = () => {
  const { t } = useTranslation();

  const { isPhone } = useDevice();

  const {
    lineup,
    numPickedOptionsWithoutGermanLicense,
    lineupSyncStatus,
    optionsSyncStatus,
    requestError,
    resetRequestError,
  } = useContext(LineupContext);
  const { slots } = useLineSlots();

  const showSyncInfo =
    lineupSyncStatus === "couldNotSync" || optionsSyncStatus === "couldNotSync"
      ? "showSyncError"
      : lineupSyncStatus === "synching" || optionsSyncStatus === "synching"
      ? "showSynching"
      : "nothing";

  return (
    <div className="neo__lineupField">
      <div className="neo__lineupField__inner">
        {showSyncInfo === "showSynching" && (
          <div className="neo__lineupSyncInfo__synching" />
        )}
        {showSyncInfo === "showSyncError" && (
          <div className="neo__lineupSyncInfo__error">
            <div>
              <p>{t("lineup.could_not_sync")}</p>
              <a onClick={() => window.location.reload()}>
                {t("lineup.could_not_sync_action_reload")}
              </a>
            </div>
          </div>
        )}
        {requestError && (
          <div className="neo__lineupSyncInfo__error">
            <div>
              <p>{t(`lineup.request_error.${requestError.reason}`)}</p>
              <a onClick={resetRequestError}>
                {t("lineup.request_error.close_btn")}
              </a>
            </div>
          </div>
        )}
        {lineup && !lineup.from_other && (
          <div className="neo__lineupField__positionValidityInfoHolder">
            {lineup.validity.valid ? (
              <ValidityInfo valid={true} markCritical={false}>
                {t("lineup.default_valid_info")}
              </ValidityInfo>
            ) : lineup.phase === "open" ? (
              <ValidityInfo
                valid={false}
                markCritical={lineup.validity.is_critical || false}
              >
                {lineup.validity.invalid_reason
                  ? t(`lineup.invalid_reason.${lineup.validity.invalid_reason}`)
                  : t("lineup.default_invalid_reason")}
              </ValidityInfo>
            ) : lineup.phase === "upcoming" ? (
              <ValidityInfo valid={false} markCritical={false}>
                {t("lineup.invalid_reason.invalid_but_upcoming")}
              </ValidityInfo>
            ) : lineup.phase === "closed" || lineup.phase === "scored" ? (
              <ValidityInfo valid={false} markCritical={false}>
                {t("lineup.invalid_reason.invalid_but_no_longer_open")}
              </ValidityInfo>
            ) : null}
          </div>
        )}
        <div className="neo__lineupField__rowsHolder">
          <div className="neo__lineupField__rows">
            <Row pos="goal">
              <SlotPlaceholder layout="smallAndInline" />
              {_.map(slots.goal, (sl) => (
                <ConnectedSlot
                  key={sl}
                  slot={sl}
                  layout={sl === "goalsub" ? "smallAndInline" : "regular"}
                />
              ))}
            </Row>
            <Row pos="def">
              {_.map(slots.def, (sl) => (
                <ConnectedSlot key={sl} slot={sl} layout="regular" />
              ))}
            </Row>
            <Row pos="for" shiftSecondItemABitDown={slots.for.length === 3}>
              {_.map(slots.for, (sl) => (
                <ConnectedSlot key={sl} slot={sl} layout="regular" />
              ))}
            </Row>
          </div>
        </div>
        {!isPhone &&
          showGermanLicenseInfo(
            lineup,
            numPickedOptionsWithoutGermanLicense
          ) && (
            <div className="neo__lineupField__germanLicenseInfoHolder">
              <GermanLicenseInfo
                count={numPickedOptionsWithoutGermanLicense}
                max={lineup.restrictions.max_without_german_license}
              />
            </div>
          )}
        <div className="neo__lineupField__magentaSportLogo" />
      </div>
      {isPhone &&
        showGermanLicenseInfo(lineup, numPickedOptionsWithoutGermanLicense) && (
          <div className="neo__lineupField__germanLicenseInfoHolder">
            <GermanLicenseInfo
              count={numPickedOptionsWithoutGermanLicense}
              max={lineup.restrictions.max_without_german_license}
            />
          </div>
        )}
      <div className="neo__lineupField__statsIconLegendHolder">
        <StatsIconLegend />
      </div>
    </div>
  );
};
